// C:\Users\aleja\Code\inventory_analytics\src\front\src\store\flux\partDocumentActions.js
import getApiBaseUrl from "../../utils/api";


const API_URL = getApiBaseUrl();

const buildDocumentsUrl = (materialId, suffix = "") =>
  `${API_URL}/api/catalog/parts/${encodeURIComponent(materialId)}/documents${suffix}`;

const failureResult = (
  message,
  { code = "PART_DOCUMENT_REQUEST_ERROR", status = 0 } = {},
) => ({
  success: false,
  data: null,
  error: {
    code,
    message,
  },
  status,
});

const normalizeError = (payload, fallbackMessage, status) => {
  const backendError = payload?.error;

  if (backendError && typeof backendError === "object") {
    return failureResult(
      backendError.message || fallbackMessage,
      {
        code: backendError.code || "PART_DOCUMENT_REQUEST_ERROR",
        status,
      },
    );
  }

  if (typeof backendError === "string" && backendError.trim()) {
    return failureResult(backendError, { status });
  }

  if (typeof payload?.msg === "string" && payload.msg.trim()) {
    return failureResult(payload.msg, { status });
  }

  return failureResult(fallbackMessage, { status });
};

const parseJsonResponse = async (
  response,
  actions,
  fallbackMessage,
) => {
  const payload = await response.json().catch(() => null);

  if (response.status === 401 || response.status === 403) {
    await actions.handleApiError(response);
  }

  if (!response.ok || !payload?.success) {
    return normalizeError(payload, fallbackMessage, response.status);
  }

  return {
    success: true,
    data: payload.data ?? null,
    error: null,
    status: response.status,
  };
};

const parseBinaryError = async (
  response,
  actions,
  fallbackMessage,
) => {
  const contentType = response.headers.get("Content-Type") || "";
  let payload = null;

  if (contentType.toLowerCase().includes("application/json")) {
    payload = await response.json().catch(() => null);
  }

  if (response.status === 401 || response.status === 403) {
    await actions.handleApiError(response);
  }

  return normalizeError(payload, fallbackMessage, response.status);
};

const sanitizeFilename = (value, fallbackFilename) => {
  const normalized = String(value || "")
    .replace(/[\\/]/g, "_")
    .replace(/[\u0000-\u001f\u007f]/g, "")
    .trim();

  return normalized || fallbackFilename;
};

const getFilenameFromDisposition = (
  contentDisposition,
  fallbackFilename,
) => {
  if (!contentDisposition) {
    return sanitizeFilename(fallbackFilename, "documento");
  }

  const utf8Match = contentDisposition.match(
    /filename\*=UTF-8''([^;]+)/i,
  );
  if (utf8Match?.[1]) {
    try {
      return sanitizeFilename(
        decodeURIComponent(utf8Match[1].replace(/["']/g, "")),
        fallbackFilename,
      );
    } catch {
      return sanitizeFilename(utf8Match[1], fallbackFilename);
    }
  }

  const filenameMatch = contentDisposition.match(
    /filename="?([^";]+)"?/i,
  );
  return sanitizeFilename(
    filenameMatch?.[1],
    sanitizeFilename(fallbackFilename, "documento"),
  );
};

const validateMaterialId = (materialId) => {
  const normalized = String(materialId || "").trim();
  if (!normalized) {
    return failureResult("Material inválido", {
      code: "PART_DOCUMENT_INVALID_MATERIAL",
    });
  }
  return normalized;
};

const validateDocumentId = (documentId) => {
  const normalized = Number(documentId);
  if (!Number.isInteger(normalized) || normalized < 1) {
    return failureResult("Documento inválido", {
      code: "PART_DOCUMENT_INVALID_ID",
    });
  }
  return normalized;
};

const partDocumentActions = ({ getStore, getActions }) => ({
  getPartDocuments: async (materialId) => {
    const normalizedMaterialId = validateMaterialId(materialId);
    if (typeof normalizedMaterialId !== "string") {
      return { ...normalizedMaterialId, data: [] };
    }

    try {
      const { token } = getStore();
      const response = await fetch(
        buildDocumentsUrl(normalizedMaterialId),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const result = await parseJsonResponse(
        response,
        getActions(),
        "No se pudo cargar la documentación del repuesto",
      );

      if (!result.success) {
        return { ...result, data: [] };
      }

      return {
        ...result,
        data: Array.isArray(result.data?.documents)
          ? result.data.documents
          : [],
      };
    } catch (error) {
      console.error("Part documents fetch error:", error);
      return {
        ...failureResult(
          "No se pudo cargar la documentación del repuesto",
        ),
        data: [],
      };
    }
  },

  uploadPartDocument: async (
    materialId,
    file,
    documentType,
    description,
  ) => {
    const normalizedMaterialId = validateMaterialId(materialId);
    if (typeof normalizedMaterialId !== "string") {
      return normalizedMaterialId;
    }
    if (!file) {
      return failureResult("Selecciona un archivo", {
        code: "PART_DOCUMENT_FILE_REQUIRED",
      });
    }

    const normalizedDocumentType = String(documentType || "").trim();
    if (!normalizedDocumentType) {
      return failureResult("Selecciona un tipo documental", {
        code: "PART_DOCUMENT_TYPE_REQUIRED",
      });
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("document_type", normalizedDocumentType);
    if (String(description || "").trim()) {
      formData.append("description", String(description).trim());
    }

    try {
      const { token } = getStore();
      const response = await fetch(
        buildDocumentsUrl(normalizedMaterialId),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        },
      );
      return await parseJsonResponse(
        response,
        getActions(),
        "No se pudo subir el documento",
      );
    } catch (error) {
      console.error("Part document upload error:", error);
      return failureResult("No se pudo subir el documento");
    }
  },

  previewPartDocument: async (materialId, documentId) => {
    const normalizedMaterialId = validateMaterialId(materialId);
    if (typeof normalizedMaterialId !== "string") {
      return normalizedMaterialId;
    }
    const normalizedDocumentId = validateDocumentId(documentId);
    if (typeof normalizedDocumentId !== "number") {
      return normalizedDocumentId;
    }

    try {
      const { token } = getStore();
      const response = await fetch(
        buildDocumentsUrl(
          normalizedMaterialId,
          `/${normalizedDocumentId}/preview`,
        ),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        return await parseBinaryError(
          response,
          getActions(),
          "No se pudo cargar la vista previa",
        );
      }

      const blob = await response.blob();
      const contentType =
        response.headers.get("Content-Type") ||
        blob.type ||
        "application/octet-stream";
      const filename = getFilenameFromDisposition(
        response.headers.get("Content-Disposition"),
        "vista-previa",
      );

      return {
        success: true,
        data: { blob, contentType, filename },
        error: null,
        status: response.status,
      };
    } catch (error) {
      console.error("Part document preview error:", error);
      return failureResult("No se pudo cargar la vista previa");
    }
  },

  downloadPartDocument: async (
    materialId,
    documentId,
    originalFilename,
  ) => {
    const normalizedMaterialId = validateMaterialId(materialId);
    if (typeof normalizedMaterialId !== "string") {
      return normalizedMaterialId;
    }
    const normalizedDocumentId = validateDocumentId(documentId);
    if (typeof normalizedDocumentId !== "number") {
      return normalizedDocumentId;
    }

    try {
      const { token } = getStore();
      const response = await fetch(
        buildDocumentsUrl(
          normalizedMaterialId,
          `/${normalizedDocumentId}/download`,
        ),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        return await parseBinaryError(
          response,
          getActions(),
          "No se pudo descargar el documento",
        );
      }

      const blob = await response.blob();
      const filename = getFilenameFromDisposition(
        response.headers.get("Content-Disposition"),
        originalFilename || "documento",
      );
      const objectUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");

      try {
        anchor.href = objectUrl;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
      } finally {
        anchor.remove();
        window.setTimeout(() => {
          window.URL.revokeObjectURL(objectUrl);
        }, 0);
      }

      return {
        success: true,
        data: { filename },
        error: null,
        status: response.status,
      };
    } catch (error) {
      console.error("Part document download error:", error);
      return failureResult("No se pudo descargar el documento");
    }
  },

  deletePartDocument: async (
    materialId,
    documentId,
    deletionReason,
  ) => {
    const normalizedMaterialId = validateMaterialId(materialId);
    if (typeof normalizedMaterialId !== "string") {
      return normalizedMaterialId;
    }
    const normalizedDocumentId = validateDocumentId(documentId);
    if (typeof normalizedDocumentId !== "number") {
      return normalizedDocumentId;
    }
    const normalizedReason = String(deletionReason || "").trim();
    if (!normalizedReason) {
      return failureResult("Indica el motivo de eliminación", {
        code: "PART_DOCUMENT_DELETION_REASON_REQUIRED",
      });
    }

    try {
      const { token } = getStore();
      const response = await fetch(
        buildDocumentsUrl(
          normalizedMaterialId,
          `/${normalizedDocumentId}`,
        ),
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            deletion_reason: normalizedReason,
          }),
        },
      );
      return await parseJsonResponse(
        response,
        getActions(),
        "No se pudo eliminar el documento",
      );
    } catch (error) {
      console.error("Part document delete error:", error);
      return failureResult("No se pudo eliminar el documento");
    }
  },
});

export default partDocumentActions;
